import React from 'react';
import { useDataProvider, useRedirect } from 'react-admin';
import {
  LayoutDashboard,
  Users as UsersIcon,
  UserCheck,
  Award,
  Calendar,
  BookOpen,
  Star,
  MapPin,
  CornerDownLeft,
  Smartphone,
  Activity,
  Settings as SettingsIcon,
  MessageSquare,
  CircleUser,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from './command';
import { Avatar } from './data-cells';

/**
 * ⌘K command palette — DESIGN_SYSTEM.md §7/§8.
 * Server-side, grouped search across entities via the RA dataProvider (filter.q).
 * Opens on ⌘/Ctrl-K or a `latio:open-search` window event (app-bar trigger).
 */
export const OPEN_SEARCH_EVENT = 'latio:open-search';

type Entity = {
  resource: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  primary: (r: any) => string;
  secondary?: (r: any) => string | undefined;
  avatar?: (r: any) => string | undefined;
};

const ENTITIES: Entity[] = [
  {
    resource: 'users',
    label: 'Users',
    icon: UsersIcon,
    primary: (r) => r.full_name || r.name || r.email || `#${r.id}`,
    secondary: (r) => r.email,
    avatar: (r) => r.profile_image_url || r.avatar_url,
  },
  {
    resource: 'trainers',
    label: 'Trainers',
    icon: Award,
    primary: (r) => r.full_name || r.name || r.email || `#${r.id}`,
    secondary: (r) => r.sport || r.email,
  },
  {
    resource: 'trainer-applications',
    label: 'Trainer Applications',
    icon: UserCheck,
    primary: (r) => r.full_name || r.name || r.email || `#${r.id}`,
    secondary: (r) => r.status || r.sport,
  },
  {
    resource: 'sessions',
    label: 'Sessions',
    icon: Calendar,
    primary: (r) => r.title || r.name || `#${r.id}`,
    secondary: (r) => r.status || r.sport,
  },
  {
    resource: 'bookings',
    label: 'Bookings',
    icon: BookOpen,
    primary: (r) => r.title || r.user_name || `Booking #${r.id}`,
    secondary: (r) => r.status,
  },
  {
    resource: 'session-reviews',
    label: 'Reviews',
    icon: Star,
    primary: (r) => r.comment || r.text || `Review #${r.id}`,
    secondary: (r) => r.author_name || r.status,
  },
  {
    resource: 'locations',
    label: 'Locations',
    icon: MapPin,
    primary: (r) => r.name || r.title || `#${r.id}`,
    secondary: (r) => r.address || r.city,
  },
];

const QUICK_JUMP: { label: string; to: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { label: 'Overview', to: '/', icon: LayoutDashboard },
  { label: 'Users', to: '/users', icon: UsersIcon },
  { label: 'Trainer Applications', to: '/trainer-applications', icon: UserCheck },
  { label: 'Sessions', to: '/sessions', icon: Calendar },
  { label: 'Bookings', to: '/bookings', icon: BookOpen },
  { label: 'Session Reviews', to: '/session-reviews', icon: Star },
];

// Static destinations (not entity records). Shown under "Pages" when idle and
// matched client-side by label/keywords while typing — industry-standard ⌘K behaviour.
type Page = {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string;
};

const PAGES: Page[] = [
  { label: 'App Version', to: '/app-versions', icon: Smartphone, keywords: 'app version build release update force ios android' },
  { label: 'System Health', to: '/system-health', icon: Activity, keywords: 'system health status uptime monitoring services' },
  { label: 'Chat Monitoring', to: '/chat-monitoring', icon: MessageSquare, keywords: 'chat monitoring messages moderation' },
  { label: 'Settings', to: '/settings', icon: SettingsIcon, keywords: 'settings configuration preferences' },
  { label: 'Account', to: '/account', icon: CircleUser, keywords: 'account profile me' },
];

type Group = { entity: Entity; items: any[] };

export function CommandSearch() {
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Open on ⌘K / Ctrl-K and on the app-bar trigger event.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, onEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, onEvent);
    };
  }, []);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setGroups([]);
    }
  }, [open]);

  // Debounced cross-entity search.
  React.useEffect(() => {
    const q = query.trim();
    if (!q) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      const results = await Promise.all(
        ENTITIES.map(async (entity) => {
          try {
            const { data } = await dataProvider.getList(entity.resource, {
              pagination: { page: 1, perPage: 5 },
              sort: { field: 'id', order: 'DESC' },
              filter: { q },
            });
            return { entity, items: data || [] } as Group;
          } catch {
            return { entity, items: [] } as Group;
          }
        }),
      );
      setGroups(results.filter((g) => g.items.length > 0));
      setLoading(false);
    }, 250);
    return () => clearTimeout(handle);
  }, [query, dataProvider]);

  const go = (path: string) => {
    setOpen(false);
    redirect(path);
  };

  // Client-side page matching (static destinations).
  const q = query.trim().toLowerCase();
  const matchedPages = q
    ? PAGES.filter((p) => `${p.label} ${p.keywords}`.toLowerCase().includes(q))
    : [];
  const hasResults = groups.length > 0 || matchedPages.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search users, trainers, sessions, bookings…"
        value={query}
        onValueChange={setQuery}
      />
      {/* Server-side results: disable cmdk's built-in filtering. */}
      <CommandList>
        {query.trim() === '' ? (
          <>
            <CommandGroup heading="Quick jump">
              {QUICK_JUMP.map((item) => (
                <CommandItem key={item.to} value={item.label} onSelect={() => go(item.to)}>
                  <item.icon className="size-4 text-ink-2" />
                  <span>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Pages">
              {PAGES.map((p) => (
                <CommandItem key={p.to} value={p.label} onSelect={() => go(p.to)}>
                  <p.icon className="size-4 text-ink-2" />
                  <span>{p.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : loading && !hasResults ? (
          <div className="py-6 text-center text-sm text-ink-2">Searching…</div>
        ) : !hasResults ? (
          <CommandEmpty>No results for “{query}”.</CommandEmpty>
        ) : (
          <>
            {matchedPages.length > 0 && (
              <CommandGroup heading="Pages">
                {matchedPages.map((p) => (
                  <CommandItem key={p.to} value={`page-${p.to}`} onSelect={() => go(p.to)}>
                    <p.icon className="size-4 text-ink-2" />
                    <span>{p.label}</span>
                    <CommandShortcut>
                      <CornerDownLeft className="size-3.5" />
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {groups.map((g) => (
            <CommandGroup key={g.entity.resource} heading={g.entity.label}>
              {g.items.map((item) => {
                const primary = g.entity.primary(item);
                const secondary = g.entity.secondary?.(item);
                return (
                  <CommandItem
                    key={`${g.entity.resource}-${item.id}`}
                    value={`${g.entity.resource}-${item.id}-${primary}`}
                    onSelect={() => go(`/${g.entity.resource}/${item.id}/show`)}
                  >
                    {g.entity.avatar ? (
                      <Avatar src={g.entity.avatar(item)} name={primary} size={24} />
                    ) : (
                      <g.entity.icon className="size-4 text-ink-2" />
                    )}
                    <span className="truncate">{primary}</span>
                    {secondary && (
                      <span className="ml-1 truncate text-[13px] text-ink-2">{secondary}</span>
                    )}
                    <CommandShortcut>
                      <CornerDownLeft className="size-3.5" />
                    </CommandShortcut>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            ))}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
